import produce from 'immer';
import isNil from 'lodash/isNil';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';

import {
  getSnippetsAfterAttachmentDeletion,
  getSnippetsAfterAttachmentReplacement,
  getUpdatedSnippetsForAnalytics,
  getUpdatedSnippetsForHighres
} from './reducers';

import { createReducer } from 'utils/redux';
import { getSnippetsFromAttachments } from '../utils/snippets';

import { StorySnippetsState } from '../types/StorySnippetState.type';
import { StorySnippetsAction } from '../types/redux';

const initialState: StorySnippetsState = {
  deliverables: {}
};

export default createReducer<StorySnippetsState, StorySnippetsAction>('storySnippets', initialState, {
  'story-snippets/init-for-deliverable': (state, payload) => produce(
    state, draft => {
      const snippets = getSnippetsFromAttachments(
        (payload.deliverable.postAttachments ?? []).map(a => ({
          ...a,
          id: a.id as number
        }))
      );

      draft.deliverables[payload.deliverable.id] = {
        ...payload,
        snippets,
        deletedSnippets: []
      };
    }),
  'story-snippets/update-attachments-for-deliverable': (
    state, { deliverableId, attachments, type, snippetId }
  ) => produce(
    state, draft => {
      if (isNil(draft.deliverables[deliverableId])) {
        throw new Error(`StorySnippets / Can't assign attchments to a deliverable with id: ${deliverableId}: delvierable not found`);
      }

      if (type === 'analytics') {
        draft.deliverables[deliverableId].snippets = getUpdatedSnippetsForAnalytics(
          draft.deliverables[deliverableId].snippets,
          attachments,
          snippetId
        );
      }

      if (type === 'highres') {
        draft.deliverables[deliverableId].snippets = getUpdatedSnippetsForHighres(
          draft.deliverables[deliverableId].snippets,
          attachments,
          snippetId
        );
      }
    }),
  'story-snippets/update-can-edit-for-deliverable': (state, { canEdit, deliverableId }) => produce(
    state, draft => {
      if (isNil(draft.deliverables[deliverableId])) {
        throw new Error(`StorySnippets / Can't update canEdit of a deliverable with id: ${deliverableId}: delvierable not found`);
      }

      draft.deliverables[deliverableId].canEdit = canEdit;
    }),
  'story-snippets/delete-snippet': (state, { deliverableId, snippetId }) => produce(
    state, draft => {
      if (isNil(draft.deliverables[deliverableId])) {
        throw new Error(`StorySnippets / Can't delete snippet of a deliverable with id: ${deliverableId}: delvierable not found`);
      }

      draft.deliverables[deliverableId].snippets = draft.deliverables[deliverableId].snippets
        .filter(snippet => snippet.id !== snippetId)
        .map((snippet, index) => ({
          ...snippet,
          id: index
        }));
    }),
  'story-snippets/delete-deliverable-attachment': (state, {
    deliverableId,
    snippetId,
    attachmentId
  }) => produce(
    state, draft => {
      if (isNil(draft.deliverables[deliverableId])) {
        throw new Error(`StorySnippets / Can't delete attachment of a deliverable with id: ${deliverableId}: delvierable not found`);
      }

      draft.deliverables[deliverableId].snippets = getSnippetsAfterAttachmentDeletion(
        draft.deliverables[deliverableId].snippets,
        snippetId,
        attachmentId
      );
    }),
  'story-snippets/replace-deliverable-attachment': (state, {
    deliverableId,
    snippetId,
    newAttachment,
    oldAttachmentId
  }) => produce(
    state, draft => {
      if (isNil(draft.deliverables[deliverableId])) {
        throw new Error(`StorySnippets / Can't replace attachments of a deliverable with id: ${deliverableId}: delvierable not found`);
      }

      draft.deliverables[deliverableId].snippets = getSnippetsAfterAttachmentReplacement(
        draft.deliverables[deliverableId].snippets,
        newAttachment,
        snippetId,
        oldAttachmentId
      );
    }),
  'story-snippets/add-snippet': (state, { deliverableId }) => produce(state, draft => {
    if (isNil(draft.deliverables[deliverableId])) {
      throw new Error(`StorySnippets / Can't add a snippet to a deliverable with id: ${deliverableId}: delvierable not found`);
    }

    draft.deliverables[deliverableId].snippets.push({
      id: draft.deliverables[deliverableId].snippets.length,
      story: null,
      insights: [],
      saved: false
    });
  }),
  'story-snippets/flush-deliverable': (state, { deliverableId }) => produce(state, draft => {
    if (isNil(draft.deliverables[deliverableId])) {
      throw new Error(`StorySnippets / Can't flush state of a deliverable with id: ${deliverableId}: delvierable not found`);
    }

    delete draft.deliverables[deliverableId];
  }),
  'story-snippets/validate-deliverable': (state, { deliverableId, lastValidateAt }) => produce(
    state, draft => {
      if (isNil(draft.deliverables[deliverableId])) {
        throw new Error(`StorySnippets / Can't validate a deliverable with id: ${deliverableId}: delvierable not found`);
      }

      draft.deliverables[deliverableId].lastValidateAt = lastValidateAt;
    }),
  'story-snippets/externally-delete-snippets': (state, { deliverableId, snippetIds }) => produce(
    state, draft => {
      if (isNil(draft.deliverables[deliverableId])) {
        throw new Error(`StorySnippets / Can't delete attachment of a deliverable with id: ${deliverableId}: delvierable not found`);
      }

      if (snippetIds.length > 0) {
        const allAttachments = draft.deliverables[deliverableId].snippets.reduce((acc, val) => {
          if (val.story) {
            acc.push(val.story.id);
          }

          if (val.insights.length > 0) {
            acc = [
              ...acc,
              ...val.insights.map(ins => ins.id)
            ];
          }

          return acc;
        }, [] as Array<number>);

        if (isEqual(allAttachments, snippetIds)) {
          draft.deliverables[deliverableId].deletedSnippets = cloneDeep(
            draft.deliverables[deliverableId].snippets
          );
          draft.deliverables[deliverableId].snippets = [];
        } else {
          draft.deliverables[deliverableId].snippets = draft.deliverables[deliverableId].snippets
            .map(snip => {
              if (snippetIds.includes(snip.story?.id ?? -1)) {
                snip.story = null;
              }

              const insights = snip.insights.filter(ins => !snippetIds.includes(ins.id));

              return {
                ...snip,

                insights
              };
            });
        }
      } else if (
        snippetIds.length === 0
        && draft.deliverables[deliverableId].deletedSnippets.length > 0
      ) {
        draft.deliverables[deliverableId].snippets = cloneDeep(
          draft.deliverables[deliverableId].deletedSnippets
        );
        draft.deliverables[deliverableId].deletedSnippets = [];
      }
    })
});
